import { Check } from 'lucide-react';
import { MOBILE_COLORS } from './ExamPreviewFrame';

/**
 * Visual replacement for the old plain-text <select> layout dropdown.
 * Each card is a miniature mock of how the layout actually renders in the
 * exam (apps/mobile MockTestScreen's getDerivedQuestionHeading): a question
 * either carries its own prompt text, or is a bare number relying on the
 * shared group banner above it. Only these two outcomes exist in the real
 * app today — see UBT_LAYOUTS in lib/questionForm.ts.
 */
interface LayoutOption {
  value: 'set_with_stem' | 'number_only';
  title: string;
  description: string;
}

const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    value: 'set_with_stem',
    title: 'Own prompt',
    description: 'This question shows its own question text.',
  },
  {
    value: 'number_only',
    title: 'Number only',
    description: 'Just a number — relies on the group’s shared prompt above it.',
  },
];

function MiniPreview({ layout }: { layout: LayoutOption['value'] }) {
  const textStyle = { color: MOBILE_COLORS.brown, fontSize: 10, lineHeight: '13px' };
  const dotStyle = {
    width: 12,
    height: 12,
    borderRadius: 6,
    border: `1.5px solid ${MOBILE_COLORS.border}`,
    flexShrink: 0,
  };

  return (
    <div
      className="rounded-lg overflow-hidden border"
      style={{ borderColor: MOBILE_COLORS.border, backgroundColor: MOBILE_COLORS.cream }}
    >
      {layout === 'number_only' && (
        <div
          className="px-2 py-1 border-b"
          style={{ backgroundColor: MOBILE_COLORS.infoBarBg, borderColor: MOBILE_COLORS.border }}
        >
          <span style={{ ...textStyle, fontWeight: 700 }}>
            [1-2] Look at the picture and choose...
          </span>
        </div>
      )}
      <div className="p-2 space-y-2">
        {layout === 'set_with_stem' ? (
          <div>
            <span style={{ ...textStyle, fontWeight: 700 }}>1. What is the meaning of 사과?</span>
            <div className="flex gap-1 mt-1">
              <div style={dotStyle} />
              <div style={dotStyle} />
            </div>
          </div>
        ) : (
          <>
            <div>
              <span style={{ ...textStyle, fontWeight: 700 }}>1.</span>
              <div className="flex gap-1 mt-1">
                <div style={dotStyle} />
                <div style={dotStyle} />
              </div>
            </div>
            <div>
              <span style={{ ...textStyle, fontWeight: 700 }}>2.</span>
              <div className="flex gap-1 mt-1">
                <div style={dotStyle} />
                <div style={dotStyle} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function LayoutPreviewPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {LAYOUT_OPTIONS.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className="text-left rounded-xl border-2 p-2 transition-colors"
            style={{
              borderColor: selected ? MOBILE_COLORS.orange : MOBILE_COLORS.border,
              backgroundColor: selected ? '#FFF5EC' : 'white',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-gray-800">{opt.title}</span>
              {selected && <Check size={16} color={MOBILE_COLORS.orange} />}
            </div>
            <MiniPreview layout={opt.value} />
            <p className="text-xs text-gray-500 mt-1.5">{opt.description}</p>
          </button>
        );
      })}
    </div>
  );
}
